/**
 * OpenRoad icon set.
 *
 * Every path here is copied verbatim from `design/OpenRoad.dc.html` so the
 * drawn shapes are identical to the export. Icons are outline-only, inherit
 * `currentColor`, and take their stroke weight from the call site because the
 * export varies it between 1.6 and 2.2 depending on the icon's size on screen.
 */
import type { SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** Rendered width and height in px. The export's steps are 11–26. */
  size?: number;
}

type StrokeIconProps = IconProps & { strokeWidth?: number };

function StrokeIcon({
  size = 16,
  strokeWidth = 1.8,
  children,
  ...rest
}: StrokeIconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Navigation and chrome                                                      */
/* -------------------------------------------------------------------------- */

export function SearchIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={2.2} {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </StrokeIcon>
  );
}

export function BellIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </StrokeIcon>
  );
}

export function SwapIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={2} {...props}>
      <path d="m17 3 4 4-4 4" />
      <path d="M21 7H7" />
      <path d="m7 21-4-4 4-4" />
      <path d="M3 17h14" />
    </StrokeIcon>
  );
}

export function ArrowRightIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={2.2} {...props}>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </StrokeIcon>
  );
}

export function ChevronLeftIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={2} {...props}>
      <path d="m15 18-6-6 6-6" />
    </StrokeIcon>
  );
}

export function PlusIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </StrokeIcon>
  );
}

export function CloseIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={2} {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </StrokeIcon>
  );
}

export function CheckIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={2.2} {...props}>
      <path d="M20 6 9 17l-5-5" />
    </StrokeIcon>
  );
}

export function SortIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M11 5h10" />
      <path d="M11 9h7" />
      <path d="M11 13h4" />
      <path d="m3 17 3 3 3-3" />
      <path d="M6 18V4" />
    </StrokeIcon>
  );
}

/* -------------------------------------------------------------------------- */
/* Trust and sustainability                                                   */
/* -------------------------------------------------------------------------- */

export function ShieldCheckIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      <path d="m9 12 2 2 4-4" />
    </StrokeIcon>
  );
}

/**
 * The CO₂ leaf. `withStem` draws the second path; the export omits it wherever
 * the icon appears at 13–15px, where the stem would turn to mush.
 */
export function LeafIcon({ withStem = true, ...props }: StrokeIconProps & { withStem?: boolean }) {
  return (
    <StrokeIcon {...props}>
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
      {withStem ? <path d="M2 21c0-3 1.85-5.36 5.08-6" /> : null}
    </StrokeIcon>
  );
}

export function StarIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon {...props}>
      <path d="m12 2 2.4 7.4H22l-6 4.5 2.3 7.1-6.3-4.6L5.7 21 8 13.9 2 9.4h7.6z" />
    </StrokeIcon>
  );
}

/** The somsorium EcoTechFarms mark — a clock-face globe with an orbiting leaf. */
export function EcoHostingIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={1.6} {...props}>
      <path d="M12 2a10 10 0 1 0 10 10" />
      <path d="M12 2v10l7 4" />
      <path d="M22 6.5a3.5 3.5 0 0 1-5 0 3.5 3.5 0 0 1 5 0Z" />
    </StrokeIcon>
  );
}

/* -------------------------------------------------------------------------- */
/* Ride attributes                                                            */
/* -------------------------------------------------------------------------- */

/** Instant-book lightning bolt. Pass `filled` for the pill variant. */
export function BoltIcon({
  size = 15,
  filled = false,
  ...rest
}: IconProps & { filled?: boolean; strokeWidth?: number }) {
  if (filled) {
    const { strokeWidth: _strokeWidth, ...svgRest } = rest;
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="none"
        aria-hidden="true"
        focusable="false"
        {...svgRest}
      >
        <path d="M13 2 3 14h9l-1 8 10-12h-9z" />
      </svg>
    );
  }
  return (
    <StrokeIcon size={size} strokeWidth={2} {...rest}>
      <path d="M13 2 3 14h9l-1 8 10-12h-9z" />
    </StrokeIcon>
  );
}

export function WahineIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={2} {...props}>
      <circle cx="12" cy="8" r="5" />
      <path d="M12 13v8" />
      <path d="M9 18h6" />
    </StrokeIcon>
  );
}

export function PawIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={2} {...props}>
      <circle cx="11" cy="4" r="2" />
      <circle cx="18" cy="8" r="2" />
      <circle cx="20" cy="16" r="2" />
      <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" />
    </StrokeIcon>
  );
}

export function CarIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={1.7} {...props}>
      <path d="M19 17h2v-3.3a3 3 0 0 0-.9-2.1L18 9.5 16.7 6A2 2 0 0 0 14.8 5H9.2a2 2 0 0 0-1.9 1L6 9.5l-2.1 2.1A3 3 0 0 0 3 13.7V17h2" />
      <circle cx="7" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
    </StrokeIcon>
  );
}

export function ChatIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={1.7} {...props}>
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    </StrokeIcon>
  );
}

export function MusicIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={1.7} {...props}>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </StrokeIcon>
  );
}

export function NoSmokingIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={1.7} {...props}>
      <path d="M2 12h14v4H2z" />
      <path d="M18 12h4v4h-4z" />
      <path d="M16 8c0-2-1-3-1-3" />
      <path d="m2 2 20 20" />
    </StrokeIcon>
  );
}

/* -------------------------------------------------------------------------- */
/* Messaging                                                                  */
/* -------------------------------------------------------------------------- */

export function MessageIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={1.9} {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </StrokeIcon>
  );
}

export function SendIcon(props: StrokeIconProps) {
  return (
    <StrokeIcon strokeWidth={2} {...props}>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </StrokeIcon>
  );
}

/* -------------------------------------------------------------------------- */
/* Composite marks                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The filled "verified member" tick that sits beside a driver's name. Unlike
 * the outline icons this one is two-tone by design: a solid ngahere disc with
 * a white tick knocked out of it.
 */
export function VerifiedBadgeIcon({ size = 14, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#1F5C3D"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <circle cx="12" cy="12" r="11" />
      <path
        d="m8 12 3 3 5-6"
        fill="none"
        stroke="#fff"
        strokeWidth={size >= 16 ? 2.4 : 2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The OpenRoad wordmark glyph — a campervan in pōhutukawa on a ngahere tile. */
export function OpenRoadGlyph({ size = 18, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#FF6B4A"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d="M3 17h3l2-9h8l2 9h3" />
      <path d="M5 17l-1 3" />
      <path d="M19 17l1 3" />
      <circle cx="9" cy="20" r="1.5" fill="#FF6B4A" stroke="none" />
      <circle cx="15" cy="20" r="1.5" fill="#FF6B4A" stroke="none" />
    </svg>
  );
}

/** Compact glyph used in the footer, where the wheels are dropped. */
export function OpenRoadGlyphCompact({ size = 15, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#FF6B4A"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d="M3 17h3l2-9h8l2 9h3" />
    </svg>
  );
}
